<template>
  <div
    v-if="data.id"
    class="progress-item-wrap" >
    <div
      class="progress-item"
      :class="className" >
      <div class="flex flex-align-center">
        <span
          v-progress-border="{ radius: 6, thickness: 1.5, progress: data.state === ProgressStates.FINISHED ? 100 : data.progress }"
          class="success">
          <SBIcon
            v-if="!finished"
            :icon="dynamicIcon"
            :color="iconColor"
            :background-color="iconBackgroundColor"
            :hover-size="28"
            :border-radius="8"
            :size="iconSize"/>
          <span
            v-else
            class="emoji-fix">🎉</span>
        </span>
        <div class="progress-info flex column greedy ellipsis-wrapper">
          <h3
            class="ellipsis"
            :inner-html.prop="data.title" />
          <p
            class="ellipsis flex flex-align-center" >
            <span class="ellipsis">{{ data.subtitle }}</span>
          </p>
        </div>
        <template v-if="data.state !== ProgressStates.FINISHED">
          <span
            class="percentage ml-2"
            :class="{ zero: !parseInt(data.progress, 10) }" >
            {{ data.progress }}%
          </span>
          <span
            class="action-buttons center-all ml-2" >
            <!-- PAUSE button -->
            <template v-if="data.state === ProgressStates.RUNNING">
              <SBIcon
                :id="`pause-popover-trigger-${data.id}`"
                :icon="SBIconList.PAUSE_THIN"
                color="#7C8B90"
                :hover-icon-color="SBColors.blackPrimary"
                :background-color="SBColors.white"
                :hover-size="22"
                :border-radius="8"
                :size="10"
                pointer
                @click="pauseAction" />
              <b-popover
                :target="`pause-popover-trigger-${data.id}`"
                triggers="hover"
                boundary="window"
                placement="bottom"
                custom-class="small"
                no-fade
                :delay="{ show: 0, hide: 0 } " >
                <h1>Pause {{ data.type }}</h1>
                <p v-if="data.type === 'import'">Your contact import will be paused, but you can continue the import later at any time.</p>
                <p v-if="data.type === 'export'">Your contact export will be paused, but you can continue the export later at any time.</p>
                <p v-if="data.type === 'broadcast'">Your broadcast will be paused, but you can continue the broadcast later at any time.</p>
              </b-popover>
            </template>
            
            <!-- CLOSE button -->
            <template v-if="data.state !== ProgressStates.RUNNING && data.state !== ProgressStates.PAUSING">
              <SBIcon
                :id="`stop-popover-trigger-${data.id}`"
                :icon="SBIconList.BLOCK_THICK"
                color="#7C8B90"
                :hover-icon-color="SBColors.blackPrimary"
                :background-color="SBColors.white"
                :hover-size="22"
                :border-radius="8"
                :size="12"
                pointer
                @click="cancelAction" />
              <b-popover
                :target="`stop-popover-trigger-${data.id}`"
                triggers="hover"
                boundary="window"
                placement="bottom"
                custom-class="small"
                no-fade
                :delay="{ show: 0, hide: 0 } " >
                <h1>Cancel {{ data.type }}</h1>
                <p v-if="data.type === 'import'">Your contact import will be canceled. <b><u>Please note that any imported data cannot be revoked</u></b>.</p>
                <p v-if="data.type === 'broadcast'">Your broadcast will be canceled. <b><u>Please note that sent messages cannot be deleted</u></b>.</p>
                <p v-if="data.type === 'export'">Your contact export will be canceled.</p>
              </b-popover>
            </template>

            <!-- CONTINUE button -->
            <template v-if="data.state === ProgressStates.PAUSED">
              <SBIcon
                :id="`continue-popover-trigger-${data.id}`"
                class="ml-1"
                :icon="SBIconList.PLAY_EMPTY"
                color="#7C8B90"
                :hover-icon-color="SBColors.blackPrimary"
                :background-color="SBColors.white"
                :hover-size="22"
                :border-radius="8"
                :size="10"
                pointer
                @click="continueAction" />
              <b-popover
                :target="`continue-popover-trigger-${data.id}`"
                triggers="hover"
                boundary="window"
                placement="bottom"
                custom-class="small"
                no-fade
                :delay="{ show: 0, hide: 0 } " >
                <h1>Continue {{ data.type }}</h1>
                <p v-if="data.type === 'import'">Your contact import will continue from the point it was paused.</p>
                <p v-if="data.type === 'broadcast'">Your broadcast will continue from the point it was paused.</p>
                <p v-if="data.type === 'export'">Your contact export will continue from the point it was paused.</p>
              </b-popover>
            </template>

            <!-- RESTART button -->
            <template v-if="data.state === ProgressStates.FAILED">
              <SBIcon
                :id="`restart-popover-trigger-${data.id}`"
                class="ml-1"
                :icon="SBIconList.REFRESH_CLOCKWISE"
                color="#7C8B90"
                :hover-icon-color="SBColors.blackPrimary"
                :background-color="SBColors.white"
                :hover-size="22"
                :border-radius="8"
                :size="10"
                pointer
                @click="continueAction" />
              <b-popover
                :target="`restart-popover-trigger-${data.id}`"
                triggers="hover"
                boundary="window"
                placement="bottom"
                custom-class="small"
                no-fade
                :delay="{ show: 0, hide: 0 } " >
                <h1>Restart {{ data.type }}</h1>
                <p v-if="data.type === 'import'">Your contact import will be started again. If this issue persists, please contact support.</p>
                <p v-if="data.type === 'broadcast'">Your broadcast will start again. <b><u>If a contact has already received a broadcast message, it will not be sent again.</u></b> In case this issue persists, please contact support.</p>
                <p v-if="data.type === 'export'">Your contact export will be started again. If this issue persists, please contact support.</p>
              </b-popover>
            </template>
            
          </span>
        </template>
        <template v-else>
          <span class="action-buttons force-show center-all ml-2 flex">
            <!-- CLOSE button -->
            <SBIcon
              class="no-shadow"
              style="margin-right: 5px;"
              :icon="SBIconList.CLOSE_THIN"
              :color="SBColors.greyPrimary"
              :hover-icon-color="SBColors.blackPrimary"
              :hover-size="20"
              :size="10"
              pointer
              @click="closeAction" />

            <!-- DOWNLOAD button -->
            <template v-if="data.type === 'export'">
              <SBIcon
                :id="`download-popover-trigger-${data.id}`"
                :icon="SBIconList.ARROW_DOWN"
                color="#7C8B90"
                :hover-icon-color="SBColors.blackPrimary"
                :background-color="SBColors.white"
                :hover-size="22"
                :border-radius="8"
                :size="10"
                pointer
                @click="downloadAction" />
              <b-popover
                :target="`download-popover-trigger-${data.id}`"
                triggers="hover"
                boundary="window"
                placement="bottom"
                custom-class="basic-popover"
                no-fade
                :delay="{ show: 0, hide: 0 } " >
                Download
              </b-popover>
            </template>
            
            <!-- REPORT button -->
            <template v-else>
              <SBIcon
                :id="`report-popover-trigger-${data.id}`"
                :icon="SBIconList.ANALYTICS_THREE_BAR"
                color="#7C8B90"
                :hover-icon-color="SBColors.blackPrimary"
                :background-color="SBColors.white"
                :hover-size="22"
                :border-radius="8"
                :size="10"
                pointer
                @click="showReportAction" />
              <b-popover
                :target="`report-popover-trigger-${data.id}`"
                triggers="hover"
                boundary="window"
                placement="bottom"
                custom-class="basic-popover"
                no-fade
                :delay="{ show: 0, hide: 0 } " >
                Report
              </b-popover>
            </template>
          </span>
        </template>
      </div>
    </div>
    <ActionReportModal
      ref="actionReportModal"
      :action-data="calculatedData" />
  </div>
</template>

<script>
import examples from 'libphonenumber-js/examples.mobile';
import { getExampleNumber } from 'libphonenumber-js/max';
import { mapActions, mapGetters } from 'vuex';

import SBColors from '@/helpers/lists/colors';
import SBIcon from '@/components/common/Icon';
import SBIconList from '@/helpers/lists/sb-icon-list';
import ProgressStates from '@/helpers/lists/progressStates';
import JuneMixin from '@/mixins/June';
import ActionReportModal from './modals/ActionReport.modal';

export default {
  name: 'ProgressMonitor',
  components: {
    SBIcon,
    ActionReportModal,
  },
  mixins: [
    JuneMixin,
  ],
  props: {
    data: {
      type: Object,
      required: true,
      default: () => {},
    },
  },
  data() {
    return {
      SBColors,
      SBIconList,
      ProgressStates,
      dynamicIcon: SBIconList.PAUSE_THIN,
      iconBackgroundColor: SBColors.white,
      iconColor: SBColors.blackPrimary,
      iconSize: 14,
      finished: false,
      pausePopoverContent: '',
      stopPopoverContent: '',
      pusherFallback: null,
      /** If pusher doesn't respond in n seconds then trigger manual check from backend. */
      pusherInactivitySecondsLimit: 5,
      /** If there is no change in n updates on the activity then mark the activity as failed. */
      detectFailLimit: 3,
      examples: {
        national: '',
        international: '',
      },
    };
  },
  computed: {
    ...mapGetters({
      userRegion: 'user/region',
    }),
    calculatedData() {
      const data = {
        title: {},
        items: [],
      };

      if (!Object.keys(this.data.stats).length) {
        return data;
      }

      if (this.data.type === 'broadcast') {
        // BROADCAST
        data.title.text = `recipient${this.data.stats.total > 1 ? 's' : ''}`;
        data.title.value = this.data.stats.total;

        data.items.push({
          icon: SBIconList.DONE_TICK_THIN,
          iconColor: SBColors.blackUltralight,
          title: 'Sent',
          value: this.$options.filters.bigDigits(Number(this.data.stats.sent)),
          level: 0,
        });
        data.items.push({
          icon: SBIconList.WHATSAPP_DELIVERED_TICK,
          iconSize: 16,
          title: 'Delivered',
          value: this.$options.filters.bigDigits(Number(this.data.stats.delivered)),
          level: 0,
        });
        data.items.push({
          icon: SBIconList.WHATSAPP_DELIVERED_TICK,
          iconColor: SBColors.bluePrimary,
          iconSize: 16,
          title: 'Seen',
          value: this.$options.filters.bigDigits(Number(this.data.stats.read)),
          level: 0,
        });
        data.items.push({
          icon: SBIconList.RETURN,
          iconColor: SBColors.greenPrimary,
          iconSize: 14,
          title: 'Engaged',
          value: this.$options.filters.bigDigits(this.data.stats.engaged ? Object.values(this.data.stats.engaged).reduce((acc, cur) => acc + cur, 0) : 0),
          level: 0,
        });
        if (Object.keys(this.data.stats.engaged).length) {
          Object.keys(this.data.stats.engaged).forEach((subItemKey) => {
            data.items.push({
              title: subItemKey,
              value: this.$options.filters.bigDigits(Number(this.data.stats.engaged[subItemKey])),
              level: 1,
            });
          });
        }
        data.items.push({
          icon: SBIconList.CLOSE_THIN,
          iconColor: SBColors.redPrimary,
          iconSize: 10,
          title: 'Failed',
          value: this.$options.filters.bigDigits(Number(this.data.stats.failed ? this.data.stats.failed.limit + this.data.stats.failed.not_on_channel + this.data.stats.failed.contact_unsubscribed + this.data.stats.failed.invalid_phone + this.data.stats.failed.other + this.data.stats.failed.contact_blocked + (this.data.stats.failed.undeliverable ? this.data.stats.failed.undeliverable : 0) : 0)),
          level: 0,
        });
        if (this.data.stats.failed && this.data.stats.failed.limit) {
          data.items.push({
            title: 'Limit exceeded',
            value: this.$options.filters.bigDigits(Number(this.data.stats.failed.limit)),
            level: 1,
            info: {
              title: 'WhatsApp messaging limits exceeded',
              text: `${this.data.stats.failed.limit} broadcast messages failed to send. Your phone number is allowed to have 1,000 business-initiated conversations (with 1,000 unique customers) in a rolling 24-hour period. Learn more about WhatsApp limits <a href="https://developers.facebook.com/docs/whatsapp/messaging-limits/#:~:text=Business%20phone%20numbers%20without%20a,until%20the%20business%20is%20verified" target="_blank">here</a>.`,
            },
          });
        }
        if (this.data.stats.failed && this.data.stats.failed.contact_blocked) {
          data.items.push({
            title: `Blocked contact${this.data.stats.failed.contact_blocked > 1 ? 's' : ''}`,
            value: this.$options.filters.bigDigits(Number(this.data.stats.failed.contact_blocked)),
            level: 1,
          });
        }
        if (this.data.stats.failed && this.data.stats.failed.undeliverable) {
          data.items.push({
            title: 'Undeliverable',
            value: this.$options.filters.bigDigits(Number(this.data.stats.failed.undeliverable)),
            level: 1,
            info: {
              title: 'Undeliverable WhatsApp Messages',
              text: `${this.data.stats.failed.undeliverable} broadcast messages failed to send. One possible reason could be that the recipient is not on WhatsApp. Learn more about undeliverable messages <a href="https://developers.facebook.com/docs/whatsapp/cloud-api/support/error-codes/#other-errors" target="_blank">here</a>.`,
            },
          });
        }
        if (this.data.stats.failed && this.data.stats.failed.not_on_channel) {
          data.items.push({
            title: 'Not on WhatsApp',
            value: this.$options.filters.bigDigits(Number(this.data.stats.failed.not_on_channel)),
            level: 1,
          });
        }
        if (this.data.stats.failed && this.data.stats.failed.invalid_phone) {
          data.items.push({
            title: 'Wrong number format',
            value: this.$options.filters.bigDigits(Number(this.data.stats.failed.invalid_phone)),
            level: 1,
          });
        }
        if (this.data.stats.failed && this.data.stats.failed.contact_unsubscribed) {
          data.items.push({
            title: 'Contact unsubscribed',
            value: this.$options.filters.bigDigits(Number(this.data.stats.failed.contact_unsubscribed)),
            level: 1,
          });
        }
        if (this.data.stats.failed && this.data.stats.failed.other && (this.data.stats.failed.limit || this.data.stats.failed.not_on_channel || this.data.stats.failed.invalid_phone || this.data.stats.failed.contact_unsubscribed)) {
          data.items.push({
            title: 'Other',
            value: this.$options.filters.bigDigits(Number(this.data.stats.failed.other)),
            level: 1,
          });
        }
      } else if (this.data.type === 'import') {
        // IMPORT
        data.title.text = `contact${this.data.stats.total_contacts > 1 ? 's' : ''}`;
        data.title.value = this.data.stats.total_contacts;

        data.items.push({
          icon: SBIconList.DONE_TICK_THIN,
          iconColor: SBColors.blackUltralight,
          title: 'Imported',
          value: this.$options.filters.bigDigits(Number(this.data.stats.added_contacts + this.data.stats.duplicated_contacts)),
          level: 0,
        });
        if (this.data.stats.added_contacts + this.data.stats.duplicated_contacts) {
          data.items.push({
            title: 'New',
            value: this.$options.filters.bigDigits(Number(this.data.stats.added_contacts)),
            level: 1,
          });
          data.items.push({
            title: 'Updated',
            value: this.$options.filters.bigDigits(Number(this.data.stats.duplicated_contacts)),
            level: 1,
          });
        }
        data.items.push({
          icon: SBIconList.CLOSE_THIN,
          iconColor: SBColors.redPrimary,
          title: 'Failed',
          value: this.$options.filters.bigDigits(Number(this.data.stats.failed_contacts ? this.data.stats.failed_contacts.invalid_phone_format + this.data.stats.failed_contacts.other : 0)),
          level: 0,
        });
        if (this.data.stats.failed_contacts && this.data.stats.failed_contacts.invalid_phone_format) {
          data.items.push({
            title: 'Wrong number format',
            value: this.$options.filters.bigDigits(Number(this.data.stats.failed_contacts.invalid_phone_format)),
            level: 1,
            info: {
              title: 'Phone number format is wrong',
              text: `<p class="strong">Supported international format (E.164)</p>
                    <p>A phone number in full international format that includes a plus sign (+).</p>
                    <p><i>Example ${this.examples.international}</i></p>`,
            },
          });
        }
        if (this.data.stats.failed_contacts && this.data.stats.failed_contacts.other && this.data.stats.failed_contacts.invalid_phone_format) {
          data.items.push({
            title: 'Other',
            value: this.$options.filters.bigDigits(Number(this.data.stats.failed_contacts.other)),
            level: 1,
          });
        }
      }

      return data;
    },
    className() {
      const targetProgressState = Object.entries(ProgressStates).find(ps => ps[1] === this.data.state);
      if (targetProgressState) {
        return targetProgressState[0];
      }
      return 'UNKNOWN';
    },
  },
  watch: {
    data: {
      handler: function f(newValue, oldValue) {
        // this.setPusherFallback();
        if (this.data) {
          this.initialize();
        }
        if (this.data.state === this.ProgressStates.FINISHED || parseInt(this.data.progress, 10) === 100) {
          if (this.data.type === 'import') {
            this.juneTrack('import finished', { // TODO this needs to be moved to backend since there is no garantie that the frontend app will be running when the import is finished
              stats: this.data.stats,
            });
          }
          setTimeout(() => {
            this.finished = true;
          }, 500); // 500ms is the time nedded for the progress animation to finish
        } else {
          this.finished = false;
        }

        // Disabled because this has no value and there should be nothing that pauses action automatically.
        // else if (
        //   this.data.state === this.ProgressStates.RUNNING &&
        //   this.data.noChangeCounter === this.detectFailLimit
        // ) {
        //   const payload = {
        //     actionId: this.data.id,
        //     actionType: this.data.type,
        //     actionBy: 'automation',
        //   };
        //   if (this.data.type === 'broadcast') {
        //     payload.numberId = this.data.numberId;
        //     payload.title = 'Broadcast paused';
        //   } else if (this.data.type === 'import') {
        //     payload.title = 'Importing paused';
        //   }
        //   this.markActionAsFailed(payload);
        // }
      },
      deep: true,
    },
  },
  mounted() {
    this.initialize();
    // this.setPusherFallback();
    if (this.data.type === 'import') {
      const phoneNumberExamples = getExampleNumber(this.userRegion, examples);
      this.examples = {
        international: phoneNumberExamples.formatInternational(),
      };
    }
  },
  beforeDestroy() {
    clearTimeout(this.pusherFallback);
  },
  methods: {
    ...mapActions({
      pauseActionVuex: 'organisation/pauseAction',
      continueActionVuex: 'organisation/continueAction',
      cancelActionVuex: 'organisation/cancelAction',
      closeActionVuex: 'organisation/closeAction',
      // checkActionProgressVuex: 'organisation/checkActionProgress',
      // markActionAsFailed: 'organisation/markActionAsFailed',
    }),
    initialize() {
      if (this.data.state === this.ProgressStates.FINISHED || parseInt(this.data.progress, 10) === 100) {
        this.finished = true;
      } else {
        this.finished = false;
      }

      switch (this.data.state) {
        case this.ProgressStates.PAUSED:
          this.dynamicIcon = this.SBIconList.PAUSE_THIN;
          this.iconBackgroundColor = this.SBColors.greyUltralight;
          this.iconColor = SBColors.blackPrimary;
          this.iconSize = 12;
          break;
        case this.ProgressStates.STOPPED:
          this.dynamicIcon = this.SBIconList.PAUSE_THIN;
          this.iconBackgroundColor = this.SBColors.greyUltralight;
          this.iconColor = SBColors.blackPrimary;
          this.iconSize = 12;
          break;
        case this.ProgressStates.FAILED:
          this.dynamicIcon = this.SBIconList.TRIANGLE_EXCLAMATION;
          this.iconBackgroundColor = '#fff5f5';
          this.iconColor = SBColors.redPrimary;
          this.iconSize = 12;
          break;
        default:
          this.dynamicIcon = this.data.icon;
          this.iconBackgroundColor = this.SBColors.white;
          this.iconColor = SBColors.blackPrimary;
          this.iconSize = 14;
          break;
      }
    },
    pauseAction() {
      clearTimeout(this.pusherFallback);
      const payload = {
        actionId: this.data.id,
        actionType: this.data.type,
        actionBy: 'user',
      };
      if (this.data.type === 'broadcast') {
        payload.numberId = this.data.numberId;
        payload.title = 'Pausing...';
        payload.pausingState = ProgressStates.PAUSING;
      } else if (this.data.type === 'import') {
        payload.title = 'Pausing...';
        payload.pausingState = ProgressStates.PAUSING;
      }
      this.pauseActionVuex(payload);
    },
    continueAction() {
      const payload = {
        actionId: this.data.id,
        actionType: this.data.type,
        actionBy: 'user',
      };
      if (this.data.type === 'broadcast') {
        payload.numberId = this.data.numberId;
        payload.title = 'Sending broadcast';
      } else if (this.data.type === 'import') {
        payload.title = 'Importing contacts';
      }
      this.continueActionVuex(payload);
    },
    cancelAction() {
      clearTimeout(this.pusherFallback);
      const payload = {
        actionId: this.data.id,
        actionType: this.data.type,
        actionBy: 'user',
      };
      if (this.data.type === 'broadcast') {
        payload.numberId = this.data.numberId;
      }
      const titleMessage = {
        title: '',
        message: '',
      };
      switch (this.data.type) {
        case 'import':
          titleMessage.title = 'Cancel contact import';
          titleMessage.message = 'Are you sure you want to cancel your contact import?';
          break;
        case 'broadcast':
          titleMessage.title = 'Cancel broadcast';
          titleMessage.message = 'Are you sure you want to cancel this broadcast?';
          break;
        default:
          break;
      }
      this.$confirmPluginModal.show({
        buttonVariant: 'RED',
        confirmButtonText: 'Confirm',
        cancelButtonText: 'Back',
        showUndoWarning: true,
        autoClose: false,
        confirmCallback: () => { this.cancelActionVuex(payload); },
        ...titleMessage,
      });
    },
    closeAction() {
      clearTimeout(this.pusherFallback);
      const payload = {
        actionId: this.data.id,
        actionType: this.data.type,
        actionBy: 'user',
      };
      if (this.data.type === 'broadcast') {
        payload.numberId = this.data.numberId;
      }
      this.closeActionVuex(payload);
    },
    downloadAction() {
      // console.log('DOWNLOAD');
    },
    /**
     * Disabled because this has no value and there should be nothing that pauses action automatically.
     */
    // setPusherFallback() {
    //   clearTimeout(this.pusherFallback);
    //   if (this.data.state === ProgressStates.RUNNING) {
    //     this.pusherFallback = setTimeout(() => { this.checkActionProgress(); }, this.pusherInactivitySecondsLimit * 1000);
    //   }
    // },
    /**
     * If pusher is not providing any data then we should ask backend for the update directly.
     */
    // checkActionProgress() {
    //   const payload = {
    //     actionId: this.data.id,
    //     actionType: this.data.type,
    //   };
    //   if (this.data.type === 'broadcast') {
    //     payload.numberId = this.data.numberId;
    //   }
    //   this.checkActionProgressVuex(payload)
    //     .then(() => { this.setPusherFallback(); });
    // },
    showReportAction() {
      this.$refs.actionReportModal.show();
    },
  },
};
</script>

<style lang="scss" scoped>
.progress-item-wrap {
  cursor: default;
  .progress-item {
    padding: 9px 6px;

    .sb-icon:not(.no-shadow) {
      box-shadow: 0 0 3px 0 rgba(48, 54, 70, 0.05);
      border: solid 1px #e0e3e5;
    }

    .success span {
      width: 28px;
      height: 28px;
      background: $white;
      border-radius: 8px;
      box-shadow: 0 0 3px 0 rgba(48, 54, 70, 0.05);
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 11px !important;
      padding-top: 2px;
    }

    .progress-info {
      margin-left: 13px;

      h3 {
        font-size: 13px;
        font-weight: 400;
        color: $blackPrimary;
        line-height: initial;
      }

      p {
        font-size: 11px;
        font-weight: normal;
        color: $blackUltralight;
        line-height: initial;
        margin-top: -1px;
      }

      ::v-deep .info-icon {
        margin-top: 1px;

        .icon.mask {
          min-width: 10px;
        }
      }
    }

    &.PAUSING {
      .progress-info * {
        color: #7C8B91;
      }
    }

    .percentage {
      opacity: 0.9;
      font-size: 12px;
      font-weight: 500;
      color: $bluePrimary;

      &.zero {
        color: $blackUltralight;
      }
    }

    .action-buttons:not(.force-show) {
      display: none;
    }

    .action-buttons {
      ::v-deep .sb-icon:not(.no-shadow):hover {
        box-shadow: 0 0 3px 0 rgba(48, 54, 70, 0.2);
      }
    }

    &:not(.PAUSING):hover {
      .percentage {
        display: none;
      }

      .action-buttons {
        display: flex;
      }
    }
  }
}
</style>
